import i18n from '@/i18n'
import { formatPercent } from '@/utils/format'
import type { AttendanceStatus, HolidayType, StatusCounts } from './types'

type Tone = 'success' | 'danger' | 'warning' | 'info' | 'brand' | 'neutral'

export const STATUSES: AttendanceStatus[] = ['present', 'absent', 'late', 'excused', 'half_day']

/**
 * Labels are English source text (translation keys, ns `attendance`): translate them at render
 * with `t(statusMeta[s].label)` / `t(statusMeta[s].short)` — "P/A/L/E/H" become "P/A/R/E/D" in French.
 */
export const statusMeta: Record<
  AttendanceStatus,
  { label: string; short: string; tone: Tone; countKey: keyof StatusCounts }
> = {
  present: { label: 'Present', short: 'P', tone: 'success', countKey: 'present' },
  absent: { label: 'Absent', short: 'A', tone: 'danger', countKey: 'absent' },
  late: { label: 'Late', short: 'L', tone: 'warning', countKey: 'late' },
  excused: { label: 'Excused', short: 'E', tone: 'info', countKey: 'excused' },
  half_day: { label: 'Half day', short: 'H', tone: 'brand', countKey: 'halfDay' },
}

/** Selected-state styling of a status segment (tokens only). */
export const statusSelectedClass: Record<AttendanceStatus, string> = {
  present:
    'data-[state=checked]:border-success-600 data-[state=checked]:bg-success-600 data-[state=checked]:text-white',
  absent:
    'data-[state=checked]:border-danger-600 data-[state=checked]:bg-danger-600 data-[state=checked]:text-white',
  late: 'data-[state=checked]:border-warning-500 data-[state=checked]:bg-warning-500 data-[state=checked]:text-white',
  excused:
    'data-[state=checked]:border-info-600 data-[state=checked]:bg-info-600 data-[state=checked]:text-white',
  half_day:
    'data-[state=checked]:border-brand-600 data-[state=checked]:bg-brand-600 data-[state=checked]:text-white',
}

/** Calendar-cell styling for a student's status on a day. */
export const statusCellClass: Record<AttendanceStatus, string> = {
  present: 'bg-success-100 text-success-700',
  absent: 'bg-danger-100 text-danger-700',
  late: 'bg-warning-100 text-warning-700',
  excused: 'bg-info-100 text-info-700',
  half_day: 'bg-brand-100 text-brand-700',
}

/** English labels (translation keys, ns `attendance`); translate at render. */
export const holidayTypeLabel: Record<HolidayType, string> = {
  government: 'Government',
  religious: 'Religious',
  school: 'School',
  closure: 'Closure',
}

export const holidayTypeTone: Record<HolidayType, Tone> = {
  government: 'info',
  religious: 'brand',
  school: 'success',
  closure: 'warning',
}

export function formatRate(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '—' : formatPercent(rate)
}

const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: 'attendance', ...options })

/**
 * The API explains a locked register (and rejected saves) in English; map its known messages to
 * the interface language. Unknown messages are returned unchanged.
 */
export function translateAttendanceMessage(message: string): string {
  let m: RegExpExecArray | null
  if (message === 'Attendance cannot be marked for a future date.') return t(message)
  if (message === 'Set a current academic year before taking attendance.') return t(message)
  if (message === 'Attendance for this day was saved by someone else at the same time. Reload and try again.')
    return t(message)
  if ((m = /^(.+) is not a working day \(school closed or holiday\)\.$/.exec(message)))
    return t('{{day}} is not a working day (school closed or holiday).', { day: m[1] })
  if ((m = /^The date is outside the current academic year \((.+)\)\.$/.exec(message)))
    return t('The date is outside the current academic year ({{year}}).', { year: m[1] })
  if (
    (m = /^Attendance older than (\d+) days is locked\. Ask an administrator to correct it\.$/.exec(message))
  )
    return t('Attendance older than {{count}} days is locked. Ask an administrator to correct it.', {
      count: Number(m[1]),
    })
  return message
}

/** Same formula as the API, for live counts while editing. */
export function countStatuses(statuses: (AttendanceStatus | null)[]): StatusCounts {
  const c: StatusCounts = { present: 0, absent: 0, late: 0, excused: 0, halfDay: 0, marked: 0, rate: null }
  for (const s of statuses) {
    if (!s) continue
    c[statusMeta[s].countKey]++
    c.marked++
  }
  const denominator = c.marked - c.excused
  c.rate =
    denominator > 0 ? Math.round(((c.present + c.late + 0.5 * c.halfDay) / denominator) * 1000) / 10 : null
  return c
}
